import React, { BaseSyntheticEvent, ChangeEvent, SyntheticEvent, useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { setDrawerOpen } from 'features/ui/model/uiSlice';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';
import { useGetLanguagesQuery } from 'features/getLanguages/getLanguagesApi';
import { CountriesType, DistributionStateType, SourceType } from 'entities/distribution/types';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useGetCountriesQuery } from 'features/getCountries/api/getCountriesApi';
import { useCacheData } from 'shared/hooks';
import { useGetCitiesQuery } from 'features/getCities/api/getCitiesApi';
import { UserType } from 'shared/types';
import { useGetB2BSalesPointsQuery } from 'features/getB2BConstants/b2bConstants';
import { fromObjectDataToArrayData } from 'entities/distribution/lib/helpers/fromObjectDataToArrayData';
import { useGetSourcesQuery } from 'features/getSources/api/getSourcesApi';
import { useGetUsersAppTypeQuery } from 'features/getUsers/api/getUsersApi';
import { ImageUpload } from 'shared/ui/ImageUpload/ui/ImageUpload';
import { useUploadImageMutation } from 'features/uploadImageNotifications/api/uploadImage';
import { distributionDirection, notificationTypes } from 'entities/distribution/model/consts';
import { UploadFile } from 'shared/ui/UploadComponents/ui/UploadFile';
import { readFileAsBase64 } from 'shared/utils/readFileAsBase64';
import { CitiesListType } from 'features/getCities/types';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { SelectChangeEvent } from '@mui/material/Select';

type DistributionCardType = {
  title: string;
  state: DistributionStateType;
  setState: React.Dispatch<React.SetStateAction<DistributionStateType>>;
  onSubmit: (e?: BaseSyntheticEvent<object, any, any> | undefined) => Promise<void>;
  form: Partial<UseFormReturn<any>>;
  textButton: string;
};

type TabPanelTypeProps = {
  children?: React.ReactNode;
  index: number;
  value: number;
};

const actionTypes = [
  {
    label: 'Внешняя ссылка',
    value: 'external_link',
  },
  {
    label: 'Диплинк',
    value: 'deep_link',
  },
];

export const DistributionCard = ({ title, state, setState, form, onSubmit, textButton }: DistributionCardType) => {
  const dispatch = useDispatch();

  const fieldsToReset: { [key: string]: any[] } = {
    country_ids: [],
    city_ids: [],
    b2b_directions: [],
    b2b_tiers: [],
    user_ids: [],
    source_ids: [],
  };

  const [appType, setAppType] = useState<string[]>([]);

  const { data: languagesList, isSuccess: languagesListSuccess } = useGetLanguagesQuery(null);
  const { data: countryList, isSuccess: countryListSuccess } = useGetCountriesQuery(null);
  const { data: citiesList, isSuccess: citiesListSuccess } = useGetCitiesQuery({ sort: 'country_id,name_ru' });
  const { data: salesPointConsts, isSuccess: salesPointConstsSuccess } = useGetB2BSalesPointsQuery(null);
  const { data: sourcesList, isSuccess: sourcesListSuccess } = useGetSourcesQuery(null);
  const { data: usersAppTypeList } = useGetUsersAppTypeQuery({
    app_type: appType,
  });
  const [uploadFunc, uploadProps] = useUploadImageMutation();

  const cachedCountries = useCacheData<CountriesType>(countryList as CountriesType[]) || [];
  const cachedSources = useCacheData<SourceType>(sourcesList as SourceType[]) || [];
  const cachedUsersAppType = useCacheData<UserType>(usersAppTypeList?.items as UserType[]) || [];

  const salesPointDirectionsArray = fromObjectDataToArrayData<number>(salesPointConsts?.['SalesPoint.Direction']);
  const salesPointTierArray = fromObjectDataToArrayData<number>(salesPointConsts?.['SalesPoint.Tier']);

  const { control, getValues, setValue, watch, formState: { errors = {} } = {} } = form;

  useEffect(() => {
    if (uploadProps.data && uploadProps.isSuccess) {
      setValue?.('image_guid', uploadProps?.data);
    }
  }, [uploadProps.isSuccess, setValue, uploadProps?.data]);

  const watchFile = watch?.('sales_points_file') !== null;
  const cityIdWatch = watch?.('city_ids');
  const countryIds = watch?.('country_ids');

  const selectCity = citiesList?.filter((city) => cityIdWatch.includes(city.id));

  const isB2B = watch?.('app_types').length === 1 && watch?.('app_types')[0] === 'b2b';

  const handleInputChange = ({ target: { name, value } }: ChangeEvent<HTMLInputElement>) => {
    setValue?.(name, value);
  };

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    setState({ ...state, value: newValue });
  };

  if (
    !(languagesListSuccess && countryListSuccess && citiesListSuccess && salesPointConstsSuccess && sourcesListSuccess)
  ) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </div>
    );
  }

  const TabPanel = (props: TabPanelTypeProps) => {
    const { children, value, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && (
          <Box sx={{ marginTop: '16px' }}>
            <Typography>{children}</Typography>
          </Box>
        )}
      </div>
    );
  };

  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  const handleUploadFile = async (file: File | null, onChange: (file: File | null) => void) => {
    if (file) {
      for (const fieldName in fieldsToReset) {
        if (Object.prototype.hasOwnProperty.call(fieldsToReset, fieldName)) {
          setValue?.(fieldName, fieldsToReset[fieldName]);
        }
      }
      const fileName = file.name;
      const base64 = await readFileAsBase64(file);
      onChange(file);
      setValue?.('sales_points_file', base64);
      setValue?.('sales_points_file_name', fileName);
    } else {
      onChange(null);
      setValue?.('sales_points_file', null);
      setValue?.('sales_points_file_name', '');
    }
  };

  const handleAppTypesChange = (type: string[]) => {
    if (type.length < (form as any).getValues().app_types.length) {
      setValue?.('user_ids', []);
    }
    if (!isB2B) {
      setValue?.('sales_points_file', null);
    }
    setValue?.('app_types', type);
    setAppType(type);
  };

  const uploadImage = (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    uploadFunc(formData);
  };

  const handleAutocompleteChange = (event: SyntheticEvent, values: CitiesListType[]) => {
    const citiesIds: number[] = values.map((value) => value.id);
    setValue?.('city_ids', citiesIds);
  };

  const handleCountryChange = (event: SelectChangeEvent<number[]>) => {
    const selectedCountryIds = event.target.value as number[];

    const updatedCityIds = cityIdWatch.filter((cityId: number) => {
      const city = citiesList?.find((city) => city.id === cityId);
      return city && selectedCountryIds.includes(city.country_id);
    });

    setValue?.('city_ids', updatedCityIds);
    setValue?.('country_ids', selectedCountryIds);
  };

  return (
    <div>
      <div className="header">
        <Typography
          variant="h5"
          sx={{ fontWeight: '600' }}
        >
          {title}
        </Typography>
        <IconButton
          onClick={() => dispatch(setDrawerOpen(false))}
          sx={{ color: '#000' }}
        >
          <CloseIcon />
        </IconButton>
      </div>
      <div className="content">
        <div>
          <Box sx={{ width: '100%' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={state.value}
                onChange={handleChange}
                aria-label="basic tabs example"
              >
                {languagesList.map((language, index) => (
                  <Tab
                    key={language.codename}
                    label={language.codename}
                    {...a11yProps(index)}
                  />
                ))}
              </Tabs>
            </Box>
            {languagesList.map((language, index) => (
              <TabPanel
                key={language.codename}
                value={state.value}
                index={index}
              >
                <Controller
                  control={control}
                  name={`title_${language.codename}`}
                  shouldUnregister={false}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label={`Заголовок ${language.codename}`}
                      size="small"
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                      sx={{
                        marginBottom: '24px',
                      }}
                      value={field.value || ''}
                      onChange={handleInputChange}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name={`content_${language.codename}`}
                  shouldUnregister={false}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label={`Описание ${language.codename}`}
                      size="small"
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                      sx={{
                        marginBottom: '24px',
                      }}
                      value={field.value || ''}
                      onChange={handleInputChange}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </TabPanel>
            ))}
          </Box>
        </div>

        <Box sx={{ width: '100%' }}>
          <Typography mb={3}>Настройки условий</Typography>
          <div>
            <Controller
              control={control}
              name="country_ids"
              render={({ field: { onChange, ...restField } }) => (
                <FormControl
                  fullWidth
                  sx={{ mt: 3 }}
                >
                  <InputLabel id="select-label-type">Страна</InputLabel>
                  <Select
                    disabled={watchFile}
                    labelId="select-label-type"
                    label="Страна"
                    multiple
                    onChange={handleCountryChange}
                    renderValue={(selected) => selected.map((item: number) => cachedCountries[item].name).join(', ')}
                    {...restField}
                  >
                    {countryList?.map((country) => (
                      <MenuItem
                        key={country.id}
                        value={country.id}
                      >
                        <Checkbox checked={getValues?.('country_ids').includes(country.id)} />
                        {country.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </div>

          <div>
            <Controller
              control={control}
              name="city_ids"
              render={() => (
                <FormControl
                  fullWidth
                  sx={{ mt: 3 }}
                >
                  <Autocomplete
                    multiple
                    disablePortal
                    disableCloseOnSelect
                    disabled={countryIds.length === 0}
                    options={citiesList.filter((city) => countryIds.includes(city.country_id)) || []}
                    value={selectCity || []}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    getOptionLabel={(options) => options?.name_ru || options.name}
                    loading={citiesListSuccess}
                    loadingText="По вашему запросу ничего не найдено"
                    onChange={handleAutocompleteChange}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox
                          checkedIcon={<CheckBoxIcon fontSize="small" />}
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        {option.name_ru}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Город"
                      />
                    )}
                  />
                </FormControl>
              )}
            />
          </div>

          <div>
            <Controller
              control={control}
              name="app_types"
              rules={{ validate: (value) => (value && value.length > 0) || 'Направление обязательно' }}
              render={({ field: { value, onChange, ...restField } }) => (
                <FormControl
                  fullWidth
                  sx={{ mt: 3 }}
                  error={!!errors.app_types}
                >
                  <InputLabel id="select-label-type">Направление*</InputLabel>
                  <Select
                    labelId="select-label-type"
                    label="Направление*"
                    multiple
                    value={value || []}
                    renderValue={(selected) => selected.map((item: string) => item).join(', ')}
                    onChange={(event) => handleAppTypesChange(event.target.value)}
                    {...restField}
                  >
                    {distributionDirection.map((direction) => (
                      <MenuItem
                        key={direction}
                        value={direction}
                      >
                        <Checkbox checked={getValues?.('app_types').includes(direction)} />
                        {direction}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.app_types?.message && <FormHelperText>{errors.app_types.message.toString()}</FormHelperText>}
                </FormControl>
              )}
            />
          </div>
          <div>
            <Controller
              control={control}
              name="notification_type"
              rules={{ required: 'Тип уведомления обязательно' }}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  sx={{ mt: 3 }}
                  error={!!errors.notification_type}
                >
                  <InputLabel id="select-label-type">Тип уведомления*</InputLabel>
                  <Select
                    labelId="select-label-type"
                    label="Тип уведомления*"
                    {...field}
                  >
                    {notificationTypes.map((notification) => (
                      <MenuItem
                        key={notification.value}
                        value={notification.value}
                      >
                        {notification.title}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.notification_type?.message && (
                    <FormHelperText>{errors.notification_type.message.toString()}</FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </div>

          <>
            <div>
              <Controller
                control={control}
                name="b2b_directions"
                render={({ field }) => (
                  <FormControl
                    fullWidth
                    sx={{ mt: 3 }}
                  >
                    <InputLabel id="select-label-type">Каналы B2B</InputLabel>
                    <Select
                      disabled={watchFile}
                      labelId="select-label-type"
                      label="Каналы B2B"
                      multiple
                      renderValue={(selected) =>
                        selected
                          .map((item: number) => salesPointDirectionsArray.find((d) => d.value === item)?.label || '')
                          .join(', ')
                      }
                      {...field}
                    >
                      {salesPointDirectionsArray.map((direction) => (
                        <MenuItem
                          key={direction.value}
                          value={direction.value}
                        >
                          <Checkbox checked={getValues?.('b2b_directions').includes(direction.value)} />
                          {direction.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </div>
            <div>
              <Controller
                control={control}
                name="b2b_tiers"
                render={({ field }) => (
                  <FormControl
                    fullWidth
                    sx={{ mt: 3 }}
                  >
                    <InputLabel id="select-label-type">Уровень торговой точки</InputLabel>
                    <Select
                      disabled={watchFile}
                      labelId="select-label-type"
                      label="Уровень торговой точки"
                      multiple
                      renderValue={(selected) =>
                        selected
                          .map((item: number) => salesPointTierArray.find((d) => d.value === item)?.label || '')
                          .join(', ')
                      }
                      {...field}
                    >
                      {salesPointTierArray.map((tier) => (
                        <MenuItem
                          key={tier.value}
                          value={tier.value}
                        >
                          <Checkbox checked={getValues?.('b2b_tiers').includes(tier.value)} />
                          {tier.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </div>
          </>

          <div>
            <Controller
              control={control}
              name="source_ids"
              render={({ field }) => (
                <FormControl
                  fullWidth
                  sx={{ mt: 3 }}
                >
                  <InputLabel id="select-label-type">Источники</InputLabel>
                  <Select
                    disabled={watchFile}
                    labelId="select-label-type"
                    label="Источники"
                    renderValue={(selected) => selected.map((item: number) => cachedSources[item].name).join(', ')}
                    multiple
                    {...field}
                  >
                    {sourcesList?.map((source) => (
                      <MenuItem
                        key={source.id}
                        value={source.id}
                      >
                        <Checkbox checked={getValues?.('source_ids').includes(source.id)} />
                        {source.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </div>

          <div>
            <Controller
              control={control}
              name="user_ids"
              render={({ field }) => (
                <FormControl
                  fullWidth
                  sx={{ mt: 3 }}
                >
                  <InputLabel id="select-label-type">Пользователи</InputLabel>
                  <Select
                    disabled={watchFile || appType.length === 0}
                    labelId="select-label-type"
                    label="Пользователи"
                    renderValue={(selected) =>
                      selected.map((item: number) => cachedUsersAppType[item].phone).join(', ')
                    }
                    multiple
                    {...field}
                  >
                    {usersAppTypeList?.items?.map((user) => (
                      <MenuItem
                        key={user.id}
                        value={user.id}
                      >
                        <Checkbox checked={getValues?.('user_ids')?.includes(user.id)} />
                        {user.phone ? user.phone : '---'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </div>
          <div>
            <Controller
              control={control}
              name="file"
              render={({ field: { onChange } }) => (
                <UploadFile
                  disabled={!isB2B}
                  isClear={!isB2B}
                  onChange={(file: File) => handleUploadFile(file, onChange)}
                  textLabel="Загрузить файл"
                  helperText="xlsx, xls, csv"
                  textButton="Загрузить торговые точки"
                  errors={errors?.file}
                />
              )}
            />
          </div>
          <Box mt={3}>
            <ImageUpload
              callback={uploadImage}
              isLoading={uploadProps.isLoading}
              isSuccess={uploadProps.isSuccess}
              silent
            />
          </Box>
          <div>
            <Typography
              mt={3}
              mb={2}
            >
              Кнопка
            </Typography>
            <Controller
              control={control}
              name="action_button.label"
              render={({ field }) => (
                <TextField
                  label="Название кнопки"
                  fullWidth
                  {...field}
                  sx={{ marginBottom: '24px' }}
                />
              )}
            />
            <Controller
              control={control}
              name="action_button.url"
              render={({ field }) => (
                <TextField
                  label="URL кнопки"
                  fullWidth
                  {...field}
                  sx={{ marginBottom: '24px' }}
                />
              )}
            />
            <Controller
              control={control}
              name="action_button.action_type"
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel id="select-label-type">Тип ссылки</InputLabel>
                  <Select
                    labelId="select-label-type"
                    label="Тип ссылки"
                    {...field}
                  >
                    {actionTypes.map((actionType) => (
                      <MenuItem
                        key={actionType.value}
                        value={actionType.value}
                      >
                        {actionType.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </div>
          <Box
            sx={{
              display: 'flex',
              marginTop: '30px',
            }}
          >
            <Button
              onClick={onSubmit}
              variant="contained"
              sx={{ textTransform: 'none' }}
            >
              {textButton}
            </Button>
          </Box>
        </Box>
      </div>
    </div>
  );
};
